import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';

export default function useResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  useEffect(() => {
    // Force logout if user is logged in when accessing password reset
    if (user) {
      logger.debug('User is logged in, forcing logout before password reset');
      logout();
    }
  }, [user, logout]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid or missing reset token');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 10) {
      setError('Password must be at least 10 characters long');
      return false;
    }

    // Check if password contains at least one digit
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one digit');
      return false;
    }

    // Check if password contains at least one special character
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }

    if (!formData.confirmPassword) {
      setError('Please confirm your password');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.resetPassword(token, formData.password);
      setSuccess(true);

      // Get preserved redirect from localStorage
      const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
      const loginUrl = preservedRedirect
        ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
        : '/login';

      // Redirect to login after 3 seconds (with preserved redirect if available)
      setTimeout(() => {
        navigate(loginUrl);
      }, 3000);

    } catch (error) {
      logger.error('Password reset error', { error });
      setError(error.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    navigate,
    formData,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    loading,
    error,
    success,
    token,
    passwordRequirements,
    isPasswordValid,
    handleInputChange,
    handleSubmit
  };
}
